import { useState } from 'react';
import { toast } from 'react-toastify';
import { useOrderFormDialog } from '../hooks/useOrderFormDialog';
import type { WasteCollectionFormDTO, WasteCollectionFormReportDTO } from '../api/waste-collection.api';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { SchoolAddressFieldset } from '@/shared/components/forms/SchoolAddressFieldset';
import { getErrorMessage } from '@/core/api/response-handler';

interface OrderFormDialogProps {
    isOpen: boolean;
    onClose: () => void;
    /** Lipseste doar in modul `readOnly`, unde nu exista «Salvare». */
    onSave?: (data: Partial<WasteCollectionFormDTO>) => Promise<void>;
    orderData: WasteCollectionFormReportDTO;
    loading?: boolean;
    /**
     * Doar vizualizare: campuri dezactivate, fara «Salvare». Pentru comenzile validate sau
     * respinse, pe care backendul nu le mai lasa editate (BE-M14) — dialogul de editare era si
     * singurul loc in care se vedea comanda intreaga, iar ascunderea lui «Editare» a luat-o cu el.
     */
    readOnly?: boolean;
}

const FARA_SALVARE = async () => {};

export function OrderFormDialog({
    isOpen,
    onClose,
    onSave,
    orderData,
    loading = false,
    readOnly = false
}: OrderFormDialogProps) {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { form, onSubmit } = useOrderFormDialog({
        orderData,
        onSave: onSave ?? FARA_SALVARE,
        schoolAddress: orderData.school?.adresa,
    });

    const handleSubmit = async (data: any) => {
        if (readOnly) return;
        setIsSubmitting(true);
        try {
            await onSubmit(data);
            onClose();
        } catch (error: unknown) {
            toast.error(getErrorMessage(error, 'Eroare la salvarea comenzii'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatDate = (dateValue?: number | string | Date) => {
        if (!dateValue) return '';

        // Handle different date formats
        let date: Date;
        if (typeof dateValue === 'number') {
            // Unix timestamp in seconds
            date = new Date(dateValue * 1000);
        } else if (typeof dateValue === 'string') {
            // ISO string
            date = new Date(dateValue);
        } else {
            // Already a Date object
            date = dateValue;
        }

        return date.toLocaleDateString('ro-RO');
    };

    const isDisabled = loading || isSubmitting || readOnly;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{readOnly ? 'Vizualizare comandă' : 'Editare Comandă Deșeuri'}</DialogTitle>
                    <DialogDescription>
                        {readOnly
                            ? 'Comanda a fost deja validată sau respinsă și nu mai poate fi modificată'
                            : 'Modificați detaliile comenzii de colectare deșeuri'}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(
                            handleSubmit,
                            () => toast.error('Vă rugăm să corectați erorile din formular')
                        )}
                        className="space-y-6"
                    >
                        <div className="space-y-4">
                                {/* Contact Person Section */}
                                <div className="space-y-3">
                                    <h3 className="font-semibold text-gray-700">Persoană Contact</h3>

                                    <FormField
                                        control={form.control}
                                        name="contactPersonName"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Nume Persoană Contact *</FormLabel>
                                                <FormControl>
                                                    <Input {...field} disabled={isDisabled} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    {/* Sub `sm`, campurile pe coloana: doua/trei campuri pe un rand la 360px
                                        dau ~150px/100px fiecare, cu etichetele rupte. */}
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <FormField
                                            control={form.control}
                                            name="contactPersonEmail"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Email Persoană Contact *</FormLabel>
                                                    <FormControl>
                                                        <Input {...field} type="email" disabled={isDisabled} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="contactPersonPhone"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Telefon Persoană Contact *</FormLabel>
                                                    <FormControl>
                                                        <Input {...field} type="tel" disabled={isDisabled} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </div>

                                {/* Collection Date (Read-only) */}
                                <FormField
                                    control={form.control}
                                    name="dataOrientativaColectare"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Data orientativă de ridicare</FormLabel>
                                            <FormControl>
                                                <Input
                                                    value={formatDate(
                                                        field.value instanceof Date
                                                            ? field.value.getTime() / 1000
                                                            : field.value
                                                    )}
                                                    disabled
                                                    className="bg-gray-100"
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />

                                {/* Pickup Address Section */}
                                <div className="space-y-3">
                                    <h3 className="font-semibold text-gray-700">Adresă Colectare</h3>

                                    <FormField
                                        control={form.control}
                                        name="pickupAddressSameWithSchoolAddress"
                                        render={({ field }) => (
                                            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                                <FormControl>
                                                    <Checkbox
                                                        checked={field.value}
                                                        onCheckedChange={field.onChange}
                                                        disabled={isDisabled}
                                                    />
                                                </FormControl>
                                                <div className="space-y-1 leading-none">
                                                    <FormLabel>Adresa de colectare corespunde cu adresa școlii?</FormLabel>
                                                    <FormMessage />
                                                </div>
                                            </FormItem>
                                        )}
                                    />

                                    <SchoolAddressFieldset
                                        namePrefix="pickupAddress"
                                        disabled={isDisabled || form.watch('pickupAddressSameWithSchoolAddress')}
                                    />
                                </div>

                                {/* Waste Quantities */}
                                <div className="space-y-3">
                                    <h3 className="font-semibold text-gray-700">Cantități Deșeuri</h3>

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                        <FormField
                                            control={form.control}
                                            name="deeeKg"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Kilograme DEEE</FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            {...field}
                                                            type="number"
                                                            disabled={isDisabled}
                                                            value={field.value || 0}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="dbaKg"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Kilograme DBA</FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            {...field}
                                                            type="number"
                                                            disabled={isDisabled}
                                                            value={field.value || 0}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="becuriSiNeoaneKg"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Kilograme Becuri și Neoane</FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            {...field}
                                                            type="number"
                                                            disabled={isDisabled}
                                                            value={field.value || 0}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </div>

                                {/* Photos Display (Read-only) */}
                                {form.watch('photos') && form.watch('photos')!.length > 0 && (
                                    <div className="space-y-3">
                                        <h3 className="font-semibold text-gray-700">Fotografii</h3>
                                        <div className="flex flex-wrap gap-2">
                                            {form.watch('photos')!.map((photo, index) => (
                                                <div key={index} className="relative h-32 w-32">
                                                    <img
                                                        src={photo}
                                                        alt={`Fotografie ${index + 1}`}
                                                        className="h-full w-full object-cover rounded-sm cursor-pointer"
                                                        onClick={() => window.open(photo, '_blank')}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Consent (Always true, disabled) */}
                                <div className="flex items-center space-x-2">
                                    <Checkbox checked={true} disabled />
                                    <label className="text-sm text-gray-700">
                                        Prin prezenta confirm că am citit Informarea cu privire la prelucrarea datelor mele cu caracter personal
                                    </label>
                                </div>
                            </div>

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onClose}
                                disabled={loading || isSubmitting}
                            >
                                {readOnly ? 'Închide' : 'Anulare'}
                            </Button>
                            {!readOnly && (
                                <Button
                                    type="submit"
                                    disabled={isDisabled}
                                >
                                    {isSubmitting ? 'Se salvează...' : 'Salvare'}
                                </Button>
                            )}
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
